import React from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';

import { library } from '@fortawesome/fontawesome-svg-core';

import {
  faClipboardListCheck,
  faClock,
  faSackDollar,
  faUser,
  faUsers,
  faHandshake,
  faHandshakeSimple,
  faUserPlus,
  faBadgePercent,
  faCalendarEdit,
  faClipboardList,
  faCalendarCheck,
  faCalendarClock,
  faPieChart,
  faMugHot,
  faPowerOff,
  faAddressBook,
  faAddressCard,
  faShareNodes,
  faPaperPlane,
  faEnvelope,
  faEnvelopeOpen,
  faEnvelopeOpenText,
  faEye,
  faStickyNote,
  faShare,
  faHandHoldingBox,
  faDownToBracket,
  faGrid2Plus,
  faNoteSticky,
  faFileCircleCheck,
  faFolderOpen,
  faArrowUpRight,
  faArrowDownRight,
  faHorizontalRule,
  faBrowsers,
  faCircleXmark,
  faArrowRight,
  faFileExport,
  faMobile,
  faBuildings,
  faMemo,
  faListTimeline,
  faBoxArchive,
  faArrowLeft,
  faArchive,
  faMoneyCheckDollarPen,
  faChartMixed,
  faFileContract,
  faLocationDot,
  faTriangleExclamation,
  faMemoCircleInfo,
  faSidebarFlip,
  faListCheck,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faChevronLeft,
  faChevronRight,
  faRedo,
  faEdit as falEdit,
  faTimes,
  faPlus,
  faArrowUp,
  faArrowDown,
  faArrowUpArrowDown,
  faArrowRotateLeft,
  faReply,
  faFileExcel,
  faMinus,
  faSquareCheck,
  faTrafficLight,
  faChartBar,
  faBookOpen,
  faPeopleRoof,
  faPeopleCarryBox,
  faComputer,
  faFloppyDisk,
  faMessages,
  faHandHoldingHeart,
  faBoxCheck,
  faLightbulb,
  faBell,
  faHeart,
  faHandsHoldingHeart,
} from '@fortawesome/pro-light-svg-icons';
import { faScrubber, faMessageQuestion, faEdit as farEdit } from '@fortawesome/pro-regular-svg-icons';
import { faCircleQuestion } from '@fortawesome/pro-solid-svg-icons';

import './i18n';
import './styles/raskavklaring-layer.css'; // kunde-raskavklaring-visning in the `raskavklaring` cascade layer
import './styles/cl-layer.css'; // component-library + cl-css + cl-theme in the `cl` cascade layer
import './styles/style.scss';
import './styles/toastify.scss';

import App from './App';

import { ApiException } from './api/v2';

import { USER_SESSION_HAS_EXIRED, USER_STAUTS_LOGGED_IN } from './constants/hook-keys';

import { createRoot } from 'react-dom/client';
import { toastIfValidationError } from './utils/validationErrorHandling';

library.add(
  faSackDollar,
  faClock,
  faClipboardListCheck,
  faUser,
  faUsers,
  faHandshake,
  faHandshakeSimple,
  faUserPlus,
  faScrubber,
  faBadgePercent,
  faCalendarEdit,
  faClipboardList,
  faCalendarCheck,
  faCalendarClock,
  faPieChart,
  faMugHot,
  faPowerOff,
  faAddressBook,
  faAddressCard,
  faShareNodes,
  faPaperPlane,
  faEnvelope,
  faEnvelopeOpen,
  faEnvelopeOpenText,
  faEye,
  faStickyNote,
  faShare,
  faHandHoldingBox,
  faDownToBracket,
  faGrid2Plus,
  faMessageQuestion,
  faNoteSticky,
  faFileCircleCheck,
  faFolderOpen,
  faArrowUpRight,
  faArrowDownRight,
  faHorizontalRule,
  faBrowsers,
  faCircleXmark,
  faArrowRight,
  faFileExport,
  faMobile,
  faBuildings,
  faMemo,
  faListTimeline,
  faBoxArchive,
  faArrowLeft,
  faArrowRotateLeft,
  faArchive,
  faMoneyCheckDollarPen,
  faChartMixed,
  faFileContract,
  faLocationDot,
  faTriangleExclamation,
  faMemoCircleInfo,
  faSidebarFlip,
  faListCheck,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faChevronLeft,
  faChevronRight,
  faRedo,
  falEdit,
  faTimes,
  faPlus,
  faArrowUp,
  faArrowDown,
  faArrowUpArrowDown,
  faReply,
  faFileExcel,
  faMinus,
  faSquareCheck,
  faTrafficLight,
  faChartBar,
  faCircleQuestion,
  faBookOpen,
  faPeopleRoof,
  faPeopleCarryBox,
  faComputer,
  faScrubber,
  farEdit,
  faCircleQuestion,
  faFloppyDisk,
  faMessages,
  faHandHoldingHeart,
  faBoxCheck,
  faLightbulb,
  faBell,
  faHeart,
  faHandsHoldingHeart,
);

const retryStrategy =
  (maxFailureCount: number) =>
  (failureCount: number, error: unknown): boolean => {
    if (ApiException.isApiException(error)) {
      // Noen ganger så er status med stor bokstav, da denne ekstra sjekken
      if (
        error.status === 401 ||
        (error as any).Status === 401 ||
        error.status === 403 ||
        (error as any).Status === 403
      ) {
        return false;
      }
    }

    // Sender inn max feil, da vi ikke vil ha masse feil på mutations
    if (failureCount < maxFailureCount) {
      return true;
    }

    return false;
  };

const handleOnError = (error: unknown): void => {
  toastIfValidationError(error);
  if (ApiException.isApiException(error)) {
    if (error.status === 401 || (error as any).Status === 401) {
      const userStatusLoggedIn = queryClient.getQueryData([USER_STAUTS_LOGGED_IN]);
      if (userStatusLoggedIn) {
        queryClient.setQueryData([USER_SESSION_HAS_EXIRED], true);
      }

      queryClient.setQueryData([USER_STAUTS_LOGGED_IN], false);
    }
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: retryStrategy(3),

      onError: handleOnError,
      onSuccess: () => toast.dismiss('ValidationError'),
    },
    mutations: {
      retry: retryStrategy(1),

      onError: handleOnError,
      onSuccess: () => toast.dismiss('ValidationError'),
    },
  },
});

const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href') as string;

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  // Bruker strict mode for testing. kommentert ut nå fordi det per i dag ligger nå
  // fetch av data i useeffect og strict mode kjører mount og unmount to ganger som gjør at
  // <React.StrictMode>
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <BrowserRouter basename={baseUrl}>
        <App />
      </BrowserRouter>
    </HelmetProvider>

    <ToastContainer closeOnClick={false} position="top-right" theme="colored" />
    <ReactQueryDevtools />
  </QueryClientProvider>,
  // </React.StrictMode>,
);
